

import { useState, useEffect, useCallback, useRef } from 'react';
import { generateFriendlyUrl, shouldGenerateUrl } from '../utils/friendlyUrlGenerator';
import { interpretarUrlAmigable } from '../utils/urlAmigableInterpreter';

export const useCatalogContextState = (pathname = null, searchParams = null) => {
    
    console.log('🚀 useCatalogContextState - Hook inicializado');
    
    /**
     * Construye la URL basada en los filtros actuales
     */
    const buildUrlFromFilters = useCallback((newFilters) => {
        let path = '/catalogo';
        const queryParams = new URLSearchParams();
        
        // Construir path con categoria y subcategoria
        if (newFilters.categoria) {
            path += `/${newFilters.categoria}`;
            if (newFilters.subcategoria) {
                path += `/${newFilters.subcategoria}`;
            }
        }
        
        // Agregar query parameters
        if (newFilters.marca) queryParams.set('marca', newFilters.marca);
        if (newFilters.canal) queryParams.set('canal', newFilters.canal);
        if (newFilters.concepto) queryParams.set('concepto', newFilters.concepto);
        if (newFilters.linea) queryParams.set('linea', newFilters.linea);
        if (newFilters.precio_min) queryParams.set('precio_min', newFilters.precio_min);
        if (newFilters.precio_max) queryParams.set('precio_max', newFilters.precio_max);
        if (newFilters.search) queryParams.set('search', newFilters.search);
        
        const queryString = queryParams.toString();
        return queryString ? `${path}?${queryString}` : path;
    }, []);
    
    // Función para navegar con filtros híbridos (compatibilidad)
    const navigateWithHybridFilters = useCallback((filters) => {
        const url = buildUrlFromFilters(filters);
        console.log('🔍 useCatalogState - Navegando a:', url);
        // La navegación se maneja en el componente que usa este hook
        return url;
    }, [buildUrlFromFilters]);
    const abortControllerRef = useRef(null);
    
    // Estado unificado del catálogo
    const [state, setState] = useState({
        // Productos
        productos: [],
        loading: false,
        error: null,
        
        // Paginación
        first: 0,
        rows: 12,
        totalRecords: 0,
        
        // Ordenamiento
        sortField: 'precioventa',
        sortOrder: 1,
        
        // Búsqueda
        searchTerm: '',
        
        // Filtros
        filters: {
            canal: null,
            categoria: null,
            subcategoria: null,
            marca: null,
            linea: null,
            concepto: null,
            precioMin: null,
            precioMax: null
        },
        
        // Datos para filtros
        filterData: {
            canales: [],
            categorias: [],
            subcategorias: [],
            marcas: [],
            lineas: [],
            conceptos: []
        },
        
        // Estados de carga de filtros
        filterLoading: {
            canales: false,
            categorias: false,
            subcategorias: false,
            marcas: false,
            lineas: false,
            conceptos: false
        },
        
        // Control de flujo de carga
        shouldUseFetchProducts: true, // Por defecto usar fetchProducts, se cambia si hay parámetros URL
        allowManualFilters: false, // Se activa después de la carga inicial con URL
        hasLoadedFromUrl: false // Flag para evitar fetchProducts después de cargarProductosConFiltrosUrl
    });
    
    // Función para actualizar filtros
    const updateFilter = useCallback((filterName, value) => {
        console.log(`🔧 updateFilter RECIBIDO - ${filterName}:`, value, 'tipo:', typeof value);
        
        // Validar que el valor sea un ID numérico válido o null
        let validatedValue = value;
        if (value !== null && value !== undefined && value !== '') {
            if (typeof value === 'object') {
                // Si es un objeto, extraer el ID
                validatedValue = value.id || value.value || null;
                console.warn(`⚠️ updateFilter recibió objeto para ${filterName}, extrayendo ID:`, validatedValue);
            } else if (typeof value === 'string' && !isNaN(value) && !['precioMin', 'precioMax'].includes(filterName)) {
                // Si es string numérico, convertir a número (excepto para filtros de precio)
                validatedValue = parseInt(value);
            } else if (['precioMin', 'precioMax'].includes(filterName)) {
                // Para filtros de precio, mantener como número
                validatedValue = typeof value === 'string' ? parseFloat(value) : value;
            }
        }
        
        console.log(`🔧 updateFilter PROCESADO - ${filterName}:`, validatedValue, 'tipo:', typeof validatedValue);
        
        setState(prevState => {
            console.log(`🔧 updateFilter ESTADO ANTERIOR - ${filterName}:`, prevState.filters[filterName]);
            console.log('🔧 updateFilter TODOS LOS FILTROS ANTERIORES:', prevState.filters);
            
            const newFilters = {
                ...prevState.filters,
                [filterName]: validatedValue
            };
    
            // Limpiar filtros dependientes cuando se cambia un filtro padre
            if (filterName === 'categoria') {
                newFilters.subcategoria = null;
                newFilters.marca = null;
                newFilters.linea = null;
                newFilters.concepto = null;
                console.log('🔧 updateFilter - Limpiando filtros dependientes de categoría');
            } else if (filterName === 'marca') {
                newFilters.linea = null;
                console.log('🔧 updateFilter - Limpiando línea por cambio de marca');
            }
    
            const newState = {
                ...prevState,
                filters: newFilters,
                first: 0, // Reset pagination when filters change
                hasLoadedFromUrl: false // Permitir fetchProducts cuando el usuario cambie filtros manualmente
            };
            
            console.log(`🔧 updateFilter NUEVO ESTADO - ${filterName}:`, newState.filters[filterName]);
            console.log('🔧 updateFilter FILTROS COMPLETOS NUEVOS:', newState.filters);
    
            return newState;
        });
    }, []);
    
    // Función para limpiar todos los filtros
    const clearAllFilters = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            filters: {
                canal: null,
                categoria: null,
                subcategoria: null,
                marca: null,
                linea: null,
                concepto: null,
                precioMin: null,
                precioMax: null
            },
            searchTerm: '',
            first: 0,
            hasLoadedFromUrl: false // Permitir fetchProducts cuando se limpien filtros
        }));
    }, []);
    
    // Función para actualizar búsqueda
    const updateSearch = useCallback((searchTerm) => {
        setState(prevState => {
            const newState = {
                ...prevState,
                searchTerm,
                first: 0, // Reset pagination when search changes
                hasLoadedFromUrl: false // Permitir fetchProducts cuando cambie la búsqueda
            };
    
            // Actualizar URL amigable después de que el estado se haya actualizado
            setTimeout(() => {
                // Usar los datos de filtro actuales del estado
                const currentFilterData = newState.filterData;
                
                // Verificar que tenemos los datos necesarios antes de navegar
                if (currentFilterData && 
                    Array.isArray(currentFilterData.categorias) && 
                    currentFilterData.categorias.length > 0) {
                    
                    console.log('🔍 Actualizando URL con búsqueda:', searchTerm);
                    
                    // Crear filtros con el término de búsqueda
                    const filtersWithSearch = {
                        ...newState.filters,
                        search: searchTerm
                    };
                    
                    navigateWithHybridFilters(filtersWithSearch, currentFilterData);
                } else {
                    console.warn('⚠️ No se puede actualizar URL: filterData no disponible');
                }
            }, 100); // Pequeño delay para asegurar que el estado se actualice
    
            return newState;
        });
    }, [navigateWithHybridFilters]);
    
    // Función para cambiar página
    const onPageChange = useCallback((event) => {
        const { first, rows } = event;
        
        setState(prevState => {
            const newState = {
                ...prevState,
                first,
                rows,
                hasLoadedFromUrl: false // Permitir fetchProducts cuando cambie la página
            };
            
            return newState;
        });
    }, []);
    
    // Función para cambiar ordenamiento
    const onSortChange = useCallback((sortField, sortOrder = 1) => {
        setState(prevState => ({
            ...prevState,
            sortField,
            sortOrder,
            first: 0, // Reset pagination when sort changes
            hasLoadedFromUrl: false // Permitir fetchProducts cuando cambie el ordenamiento
        }));
    }, []);
    
    // Función específica para cargar productos con filtros de URL amigable
    const cargarProductosConFiltrosUrl = useCallback(async (filtrosUrl, baseUrl) => {
        console.log('🚀 cargarProductosConFiltrosUrl - Iniciando carga con filtros de URL:', filtrosUrl);
        console.log('🔍 DEBUG - canal recibido:', filtrosUrl.canal);
        console.log('🔍 DEBUG - categoria recibida:', filtrosUrl.categoria);
        
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        abortControllerRef.current = new AbortController();
        
        setState(prevState => ({
            ...prevState,
            loading: true,
            error: null
        }));
        
        try {
            // Construir parámetros de consulta con filtros de URL
            const params = new URLSearchParams({
                first: 0, // Siempre empezar desde el primer producto
                rows: state.rows || 12, // Valor por defecto si no está inicializado
                sortField: state.sortField || 'precio_venta',
                sortOrder: state.sortOrder || 'asc'
            });
            
            // Agregar filtros de URL
            Object.entries(filtrosUrl).forEach(([key, value]) => {
                console.log(`🔍 DEBUG - Procesando filtro ${key}:`, value);
                if (value !== null && value !== undefined && value !== '') {
                    params.append(key, value);
                    console.log(`✅ DEBUG - Filtro ${key} agregado con valor:`, value);
                } else {
                    console.log(`⚠️ DEBUG - Filtro ${key} omitido (valor inválido):`, value);
                }
            });
            
            const url = `${baseUrl}/catalog/products?${params.toString()}`;
            console.log('🔍 cargarProductosConFiltrosUrl - URL de consulta:', url);
            console.log('🔍 DEBUG - Parámetros finales:', params.toString());
            
            const response = await fetch(url, {
                signal: abortControllerRef.current.signal
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('🔍 cargarProductosConFiltrosUrl - Respuesta del API:', data);
            
            if (data.exito) {
                const processedData = {
                    productos: data.datos.productos || [],
                    totalRecords: data.datos.total || 0,
                    first: 0,
                    rows: state.rows
                };
                
                console.log('🔍 cargarProductosConFiltrosUrl - Datos procesados:', processedData);
                
                setState(prevState => ({
                    ...prevState,
                    productos: processedData.productos, // Reemplazar productos con los filtrados
                    totalRecords: processedData.totalRecords,
                    first: processedData.first,
                    rows: processedData.rows,
                    loading: false,
                    hasLoadedFromUrl: true // Marcar que se cargó desde URL para evitar fetchProducts
                }));
                
                console.log('✅ cargarProductosConFiltrosUrl - Productos cargados exitosamente:', processedData.productos.length);
                
            } else {
                console.error('❌ cargarProductosConFiltrosUrl - Error en respuesta del API:', data.mensaje);
                setState(prevState => ({
                    ...prevState,
                    productos: [],
                    totalRecords: 0,
                    loading: false,
                    error: data.mensaje || 'Error al cargar productos'
                }));
            }
            
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('🔄 cargarProductosConFiltrosUrl - Petición cancelada');
                return;
            }
            
            console.error('❌ cargarProductosConFiltrosUrl - Error:', error);
            setState(prevState => ({
                ...prevState,
                productos: [],
                totalRecords: 0,
                loading: false,
                error: error.message || 'Error al cargar productos'
            }));
        }
    }, [state.rows, state.sortField, state.sortOrder]);

    // Función para obtener productos
    const fetchProducts = useCallback(async () => {
        console.log('🔍 fetchProducts - Iniciando carga de productos con parámetros:', {
            first: state.first,
            rows: state.rows,
            sortField: state.sortField,
            sortOrder: state.sortOrder,
            searchTerm: state.searchTerm,
            filters: state.filters
        });
    
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        abortControllerRef.current = new AbortController();
        
        setState(prevState => ({
            ...prevState,
            loading: true,
            error: null
        }));
    
        try {
            // Construir parámetros de consulta
            const params = new URLSearchParams({
                first: state.first,
                rows: state.rows,
                sortField: state.sortField,
                sortOrder: state.sortOrder
            });
    
            // Agregar término de búsqueda si existe
            if (state.searchTerm && state.searchTerm.trim()) {
                params.append('search', state.searchTerm.trim());
            }
    
            // Agregar filtros activos
            Object.entries(state.filters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    params.append(key, value);
                }
            });
    
            const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/catalog/products?${params.toString()}`;
            console.log('🔍 fetchProducts - URL de consulta:', url);
    
            const response = await fetch(url, {
                signal: abortControllerRef.current.signal
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            const data = await response.json();
            console.log('🔍 fetchProducts - Respuesta del API:', data);
    
            if (data.exito) {
                const processedData = {
                    productos: data.datos.productos || [],
                    totalRecords: data.datos.total || 0,
                    first: state.first,
                    rows: state.rows
                };
    
                console.log('🔍 fetchProducts - Datos procesados:', processedData);
    
                setState(prevState => ({
                    ...prevState,
                    productos: prevState.first === 0 
                        ? processedData.productos // Primera carga - reemplazar productos
                        : [...prevState.productos, ...processedData.productos], // Lazy loading - agregar productos
                    totalRecords: processedData.totalRecords,
                    first: processedData.first,
                    rows: processedData.rows,
                    loading: false
                }));

                console.log('✅ fetchProducts - Productos cargados exitosamente:', processedData.productos.length);
                
                // Generar URL amigable SOLO al final del proceso exitoso de fetch con first=0
                if (processedData.first === 0 && shouldGenerateUrl(processedData.first, isGeneratingUrl, state.filterData)) {
                    console.log('🔗 fetchProducts - Generando URL amigable al final del proceso exitoso');
                    setIsGeneratingUrl(true);
                    
                    // Ejecutar inmediatamente sin setTimeout para evitar lag
                    try {
                        const friendlyUrl = generateFriendlyUrl(state.filters, state.filterData, state.searchTerm);
                        console.log('🔗 fetchProducts - URL amigable generada:', friendlyUrl);
                        
                        // Actualizar la URL del navegador sin recargar la página
                        if (window.history && window.history.pushState) {
                            window.history.pushState(null, '', friendlyUrl);
                            console.log('🔗 fetchProducts - URL actualizada en el navegador');
                        }
                    } catch (error) {
                        console.error('❌ fetchProducts - Error generando URL amigable:', error);
                    } finally {
                        setIsGeneratingUrl(false);
                    }
                }
            } else {
                throw new Error(data.mensaje || 'Error al cargar productos');
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('❌ fetchProducts - Error:', error);
                setState(prevState => ({
                    ...prevState,
                    loading: false,
                    error: error.message
                }));
            }
        }
    }, [state.first, state.rows, state.sortField, state.sortOrder, state.searchTerm, 
        state.filters.canal, state.filters.categoria, state.filters.subcategoria, 
        state.filters.marca, state.filters.linea, state.filters.concepto, 
        state.filters.precioMin, state.filters.precioMax]);
    
    // Función para cargar datos de filtros
    const loadFilterData = useCallback(async (filterType, params = {}) => {
        try {
            setState(prevState => ({
                ...prevState,
                filterLoading: {
                    ...prevState.filterLoading,
                    [filterType]: true
                }
            }));

            // Mapear tipos de filtros a endpoints de la API
            let endpoint = '';
            switch (filterType) {
                case 'canales':
                    endpoint = 'catalog/channels';
                    break;
                case 'categorias':
                    endpoint = 'catalog/categories';
                    break;
                case 'subcategorias':
                    endpoint = 'catalog/subcategories';
                    break;
                case 'marcas':
                    endpoint = 'catalog/brands';
                    break;
                case 'lineas':
                    endpoint = 'catalog/brand-lines';
                    break;
                case 'conceptos':
                    endpoint = 'catalog/concepts';
                    break;
                default:
                    endpoint = filterType;
            }

            console.log(`🌐 loadFilterData - ${filterType} -> endpoint: ${endpoint}`);

            // Construir URL con parámetros si los hay
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            let url = `${baseUrl}/${endpoint}`;
            if (Object.keys(params).length > 0) {
                const searchParams = new URLSearchParams(params);
                url += `?${searchParams.toString()}`;
            }

            console.log(`🔗 loadFilterData - URL completa: ${url}`);

            const response = await fetch(url);
            console.log(`📡 loadFilterData - Response status: ${response.status}`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Procesar la respuesta de la API y asegurar que siempre sea un array
            let filterData = [];
            
            // Intentar extraer los datos de diferentes estructuras de respuesta
            if (data.exito && data.datos) {
                // Estructura: { exito: true, datos: [...] }
                filterData = Array.isArray(data.datos) ? data.datos : [];
            } else if (data.data) {
                // Estructura: { data: [...] }
                filterData = Array.isArray(data.data) ? data.data : [];
            } else if (data[endpoint]) {
                // Estructura: { [endpoint]: [...] }
                filterData = Array.isArray(data[endpoint]) ? data[endpoint] : [];
            } else if (Array.isArray(data)) {
                // Estructura: [...]
                filterData = data;
            } else {
                // Fallback: array vacío
                console.warn(`⚠️ loadFilterData - Estructura de respuesta inesperada para ${filterType}:`, data);
                filterData = [];
            }

            setState(prevState => ({
                ...prevState,
                filterData: {
                    ...prevState.filterData,
                    [filterType]: filterData
                },
                filterLoading: {
                    ...prevState.filterLoading,
                    [filterType]: false
                }
            }));

        } catch (error) {
            console.error(`❌ loadFilterData - Error loading ${filterType}:`, error);
            setState(prevState => ({
                ...prevState,
                filterData: {
                    ...prevState.filterData,
                    [filterType]: []
                },
                filterLoading: {
                    ...prevState.filterLoading,
                    [filterType]: false
                }
            }));
        }
    }, []); // Sin dependencias para evitar recreaciones innecesarias

    // Estado para controlar si ya se inicializaron los filtros de URL
    const [urlFiltersInitialized, setUrlFiltersInitialized] = useState(false);
    
    // Estado para controlar la generación de URLs amigables
    const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);

    // Efecto para inicializar filtros base al cargar la página (solo una vez)
    useEffect(() => {
        console.log('🔥 useEffect initializeBaseFilters - EJECUTÁNDOSE');
        console.log('🔥 urlFiltersInitialized:', urlFiltersInitialized);
        console.log('🔥 pathname:', pathname);
        console.log('🔥 searchParams:', searchParams ? Object.fromEntries(searchParams) : 'null');
        
        // Escribir logs iniciales usando solo localStorage
        const timestamp = new Date().toISOString();
        const initialLogMessage = `[${timestamp}] 🔥 useEffect - pathname: ${pathname}, urlFiltersInitialized: ${urlFiltersInitialized}\n`;
        const existingLogs = localStorage.getItem('debug_logs') || '';
        localStorage.setItem('debug_logs', existingLogs + initialLogMessage);
        
        // Solo ejecutar si no se han inicializado los filtros de URL
        if (urlFiltersInitialized) {
            console.log('🚫 Filtros ya inicializados, saltando');
            const skipLogMessage = `[${timestamp}] 🚫 Filtros ya inicializados, saltando\n`;
            const existingLogs2 = localStorage.getItem('debug_logs') || '';
            localStorage.setItem('debug_logs', existingLogs2 + skipLogMessage);
            return;
        }
        
        // Solo ejecutar en rutas del catálogo; si no es catálogo, mantener listo para inicializar cuando ingrese
        if (!pathname || !pathname.includes('/catalogo')) {
            console.log('🚫 No es una ruta de catálogo, saltando inicialización');
            const skipLogMessage = `[${timestamp}] 🚫 No es una ruta de catálogo: ${pathname}\n`;
            const existingLogs3 = localStorage.getItem('debug_logs') || '';
            localStorage.setItem('debug_logs', existingLogs3 + skipLogMessage);
            setUrlFiltersInitialized(false);
            return;
        }
        
        const initializeBaseFilters = async () => {
            try {
                console.log('🚀 initializeBaseFilters - Iniciando proceso de inicialización');
                
                // Escribir al localStorage
                const logMessage = `[${timestamp}] 🚀 initializeBaseFilters - Iniciando proceso de inicialización - pathname: ${pathname}\n`;
                const existingLogs4 = localStorage.getItem('debug_logs') || '';
                localStorage.setItem('debug_logs', existingLogs4 + logMessage);

                const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
                
                // PASO 1: INTÉRPRETE DE URL AMIGABLE - Ejecutar PRIMERO si tenemos parámetros de URL
                let tieneParametrosUrl = false;
                let resultadoInterpretacion = null;
                
                if (pathname && searchParams && !isGeneratingUrl) {
                    // Verificar si la URL tiene parámetros adicionales más allá de /catalogo
                    const pathParts = pathname.split('/').filter(part => part !== '');
                    const tieneParametrosPath = pathParts.length > 1; // Más que solo 'catalogo'
                    const tieneParametrosQuery = Array.from(searchParams.keys()).length > 0;
                    
                    tieneParametrosUrl = tieneParametrosPath || tieneParametrosQuery;
                    
                    if (tieneParametrosUrl) {
                        console.log('🔍 initializeBaseFilters - Detectados parámetros de URL, iniciando interpretación PRIMERO');
                        console.log('🔍 initializeBaseFilters - pathname:', pathname);
                        console.log('🔍 initializeBaseFilters - searchParams:', Object.fromEntries(searchParams));
                        
                        try {
                            // Interpretar URL amigable y obtener filtros
                            resultadoInterpretacion = await interpretarUrlAmigable(pathname, searchParams, baseUrl);
                            console.log('✅ initializeBaseFilters - Interpretación completada:', resultadoInterpretacion);
                            console.log('🔍 DEBUG - filtrosParaProductos recibidos:', JSON.stringify(resultadoInterpretacion.filtrosParaProductos, null, 2));
                            
                            // Si se obtuvieron filtros válidos, actualizar estado y cargar productos
                            if (resultadoInterpretacion && Object.keys(resultadoInterpretacion.filtrosParaProductos).length > 0) {
                                console.log('🚀 initializeBaseFilters - Cargando productos con filtros de URL amigable');
                                console.log('🔍 DEBUG - Pasando filtros a cargarProductosConFiltrosUrl:', resultadoInterpretacion.filtrosParaProductos);
                                
                                // Actualizar filtros en el estado
                                setState(prevState => ({
                                    ...prevState,
                                    filters: {
                                        ...prevState.filters,
                                        canal: resultadoInterpretacion.filtrosParaProductos.canal || null,
                                        categoria: resultadoInterpretacion.filtrosParaProductos.categoria || null,
                                        subcategoria: resultadoInterpretacion.filtrosParaProductos.subcategoria || null,
                                        marca: resultadoInterpretacion.filtrosParaProductos.marca || null,
                                        linea: resultadoInterpretacion.filtrosParaProductos.linea || null,
                                        concepto: resultadoInterpretacion.filtrosParaProductos.concepto || null,
                                        precioMin: resultadoInterpretacion.filtrosParaProductos.precioMin || null,
                                        precioMax: resultadoInterpretacion.filtrosParaProductos.precioMax || null
                                    }
                                }));
                                
                                
                                
                                // Cargar productos inmediatamente con los filtros de URL
                                await cargarProductosConFiltrosUrl(resultadoInterpretacion.filtrosParaProductos, baseUrl);
                            }
                            
                        } catch (error) {
                            console.error('❌ initializeBaseFilters - Error interpretando URL amigable:', error);
                            tieneParametrosUrl = false; // Tratar como URL simple si falla
                        }
                    }
                }
                
                // PASO 2: CARGAR FILTROS BASE - Solo DESPUÉS del intérprete
                console.log('🚀 initializeBaseFilters - Cargando filtros base (channels, categories, price-ranges)');
                
                // Marcar como cargando
                setState(prevState => ({
                    ...prevState,
                    filterLoading: {
                        ...prevState.filterLoading,
                        canales: true,
                        categorias: true,
                        priceRanges: true
                    }
                }));
                
                // Cargar canales, categorías y rangos de precios en paralelo
                const [canalesResponse, categoriasResponse, priceRangesResponse] = await Promise.all([
                    fetch(`${baseUrl}/catalog/channels`),
                    fetch(`${baseUrl}/catalog/categories`),
                    fetch(`${baseUrl}/catalog/price-ranges`)
                ]);
                
                const [canalesData, categoriasData, priceRangesData] = await Promise.all([
                    canalesResponse.json(),
                    categoriasResponse.json(),
                    priceRangesResponse.json()
                ]);
                
                const canales = canalesData.exito && canalesData.datos ? canalesData.datos : [];
                const categorias = categoriasData.exito && categoriasData.datos ? categoriasData.datos : [];
                const priceRanges = priceRangesData.exito && priceRangesData.datos ? priceRangesData.datos : null;
                
                console.log('✅ initializeBaseFilters - Datos base cargados:', {
                    canales: canales.length,
                    categorias: categorias.length,
                    priceRanges: priceRanges ? 'disponible' : 'no disponible'
                });
                
                // Actualizar estado de forma síncrona para garantizar consistencia
                setState(prevState => ({
                    ...prevState,
                    filterData: {
                        ...prevState.filterData,
                        canales: canales,
                        categorias: categorias,
                        priceRanges: priceRanges
                    },
                    filterLoading: {
                        ...prevState.filterLoading,
                        canales: false,
                        categorias: false,
                        priceRanges: false
                    },
                    // Marcar si se debe usar fetchProducts normal (solo para URLs simples)
                    shouldUseFetchProducts: !tieneParametrosUrl,
                    // Permitir que fetchProducts se ejecute después de la carga inicial con URL
                    allowManualFilters: true
                }));
                
                // PASO 3: CARGAR PRODUCTOS PARA URL SIMPLE - Solo si no hay parámetros
                if (!tieneParametrosUrl) {
                    console.log('🚀 initializeBaseFilters - URL simple detectada, se usará fetchProducts normal');
                }
                
                // Marcar como inicializado después de actualizar el estado
                setUrlFiltersInitialized(true);
                
                // Activar filtros manuales después de la carga inicial con URL
                if (tieneParametrosUrl) {
                    console.log('🔓 Activando filtros manuales después de carga inicial con URL');
                    setState(prevState => ({
                        ...prevState,
                        allowManualFilters: true
                    }));
                }
                
            } catch (error) {
                console.error('❌ Error inicializando filtros base:', error);
                setState(prevState => ({
                    ...prevState,
                    filterLoading: {
                        ...prevState.filterLoading,
                        canales: false,
                        categorias: false,
                        priceRanges: false
                    },
                    shouldUseFetchProducts: true // Usar fetchProducts por defecto en caso de error
                }));
                setUrlFiltersInitialized(true);
                
                // Activar filtros manuales en caso de error también
                setState(prevState => ({
                    ...prevState,
                    allowManualFilters: true
                }));
            }
        };
        
        if (!urlFiltersInitialized) {
            initializeBaseFilters();
        }
    }, [urlFiltersInitialized, pathname]);

    // Efecto para cargar productos cuando cambian los parámetros (solo después de inicializar filtros de URL)
    useEffect(() => {
        // Solo ejecutar fetchProducts si:
        // 1. Los filtros de URL están inicializados
        // 2. Los datos base están cargados
        // 3. Se debe usar fetchProducts (URLs simples sin parámetros) O se permiten filtros manuales
        // 4. NO se está generando una URL (para evitar bucles)
        // 5. NO se ha cargado desde URL recientemente (para evitar sobrescribir resultados del intérprete)
        if (urlFiltersInitialized && 
            state.filterData.canales.length > 0 && 
            state.filterData.categorias.length > 0 && 
            (state.shouldUseFetchProducts || state.allowManualFilters) &&
            !isGeneratingUrl &&
            !state.hasLoadedFromUrl) {
            console.log('🚀 useEffect fetchProducts - Ejecutando para URL simple o filtros manuales');
            fetchProducts();
        }
    }, [
        urlFiltersInitialized,
        state.shouldUseFetchProducts,
        state.allowManualFilters,
        state.first,
        state.rows,
        state.sortField,
        state.sortOrder,
        state.searchTerm,
        state.filters.canal,
        state.filters.categoria,
        state.filters.subcategoria,
        state.filters.marca,
        state.filters.linea,
        state.filters.concepto,
        state.filters.precioMin,
        state.filters.precioMax,
        state.filterData.canales.length,
        state.filterData.categorias.length,
        isGeneratingUrl,
        state.hasLoadedFromUrl,
        fetchProducts
    ]);

    // Efecto para cargar datos dependientes cuando cambian los filtros
    // Efecto para cargar subcategorías, marcas y conceptos cuando cambia la categoría
    useEffect(() => {
        if (state.filters.categoria) {
            // Marcar que se están cargando filtros dependientes
            setState(prevState => ({
                ...prevState,
                filterLoading: {
                    ...prevState.filterLoading,
                    subcategorias: true,
                    marcas: true,
                    conceptos: true
                }
            }));
            
            // Cargar subcategorías, marcas y conceptos cuando se selecciona una categoría
            loadFilterData('subcategorias', { id_categoria: state.filters.categoria });
            loadFilterData('marcas', { id_categoria: state.filters.categoria });
            loadFilterData('conceptos', { id_categoria: state.filters.categoria });
        } else {
            // Limpiar SOLO datos dependientes si no hay categoría seleccionada
            // NO limpiar canales ni categorías que son datos base
            setState(prevState => ({
                ...prevState,
                filterData: {
                    ...prevState.filterData,
                    subcategorias: [],
                    marcas: [],
                    lineas: [],
                    conceptos: []
                }
            }));
        }
    }, [state.filters.categoria, loadFilterData]);

    // Efecto para cargar líneas cuando cambian categoría y marca
    useEffect(() => {
        if (state.filters.categoria && state.filters.marca) {
            loadFilterData('lineas', { 
                id_categoria: state.filters.categoria,
                id_marca: state.filters.marca 
            });
        } else {
            // Limpiar líneas si no hay categoría y marca seleccionadas
            setState(prevState => ({
                ...prevState,
                filterData: {
                    ...prevState.filterData,
                    lineas: []
                }
            }));
        }
    }, [state.filters.categoria, state.filters.marca, loadFilterData]);

    // Efecto adicional para cargar líneas cuando se establece línea desde URL
    // Esto asegura que las opciones de línea se carguen incluso si la línea viene de URL
    useEffect(() => {
        // Solo ejecutar si hay una línea seleccionada pero no hay opciones de línea cargadas
        // y tenemos categoría y marca (necesarias para cargar líneas)
        if (state.filters.linea && 
            state.filters.categoria && 
            state.filters.marca && 
            (!state.filterData.lineas || state.filterData.lineas.length === 0) &&
            !state.filterLoading.lineas) {
            
            
            loadFilterData('lineas', { 
                id_categoria: state.filters.categoria,
                id_marca: state.filters.marca 
            });
        }
    }, [state.filters.linea, state.filters.categoria, state.filters.marca, state.filterData.lineas, state.filterLoading.lineas, loadFilterData]);

    // Efecto para preservar datos base cuando se selecciona subcategoría
    useEffect(() => {
        if (state.filters.subcategoria && state.filters.categoria) {
            // Cuando se selecciona subcategoría, asegurar que los datos base permanezcan
            // y recargar datos dependientes si es necesario
            if (!state.filterData.canales || state.filterData.canales.length === 0) {
                loadFilterData('canales');
            }
            if (!state.filterData.categorias || state.filterData.categorias.length === 0) {
                loadFilterData('categorias');
            }
            // Mantener datos dependientes de la categoría
            if (!state.filterData.marcas || state.filterData.marcas.length === 0) {
                loadFilterData('marcas', { id_categoria: state.filters.categoria });
            }
            if (!state.filterData.conceptos || state.filterData.conceptos.length === 0) {
                loadFilterData('conceptos', { id_categoria: state.filters.categoria });
            }
        }
    }, [state.filters.subcategoria, state.filters.categoria, loadFilterData]);

    // Efecto separado para generar URL amigable después de que se actualice el estado
    // REMOVIDO - Causaba lag al ejecutarse con cada cambio de filtros

    // Cleanup al desmontar
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Función para cargar más productos (lazy loading)
    const loadMore = useCallback(() => {
        if (state.loading || state.first + state.rows >= state.totalRecords) {
            return; // No cargar más si ya está cargando o no hay más productos
        }

        console.log('🔍 loadMore - Cargando más productos...');
        console.log('🔍 loadMore - Estado actual:', {
            first: state.first,
            rows: state.rows,
            totalRecords: state.totalRecords,
            productosActuales: state.productos.length
        });

        // Incrementar el offset para la siguiente página
        setState(prevState => ({
            ...prevState,
            first: prevState.first + prevState.rows,
            loading: true,
            // Permitir que el efecto de fetchProducts se ejecute tras cargar más
            hasLoadedFromUrl: false
        }));
    }, [state.loading, state.first, state.rows, state.totalRecords, state.productos.length]);

    return {
        ...state,
        equiposAMostrar: state.productos, // Alias para compatibilidad
        filterLoading: state.filterLoading,
        isGeneratingUrl, // Estado de generación de URL
        updateFilter,
        clearAllFilters,
        updateSearch,
        onPageChange,
        onSortChange,
        loadFilterData,
        loadMore,
        navigateWithHybridFilters,
        setIsGeneratingUrl // Función para controlar el estado de generación
    };
};
